import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  open: boolean;
  title: string;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}

/**
 * Dialog konfirmasi untuk aksi yang tidak bisa dibatalkan.
 *
 * Tombol BATAL yang difokuskan saat dialog terbuka, bukan tombol hapus. Kalau
 * yang difokuskan tombol merah, satu ketukan Enter — refleks yang sangat wajar
 * setelah menekan tombol sebelumnya — langsung menghapus data tanpa dibaca
 * dulu. Untuk aksi yang tidak bisa dibatalkan, jalur termudah harus jalur yang
 * paling aman.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  tone = 'danger',
  loading = false,
  onConfirm,
  onCancel,
  children,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    panelRef.current?.querySelector<HTMLButtonElement>('[data-autofocus]')?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', onKey);

    // Halaman di belakang dikunci agar dialog tidak ikut hanyut saat digulir.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
      onClick={() => !loading && onCancel()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-[var(--radius-card)] bg-surface p-5 shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]"
      >
        <h2 id="confirm-title" className="text-[19px] font-semibold text-ink">
          {title}
        </h2>

        <div className="mt-3 text-[15px] text-ink-secondary">{children}</div>

        <div className="mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button data-autofocus variant="secondary" onClick={onCancel} disabled={loading}>
            Batal
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
